import './styles.scss';

import { useRef, useState, useMemo } from 'react';
import CustomModal from '@components/CustomModal';
import { Text, Input, ButtonGroup } from '@fragments';
import { IconFileUpload, IconAlertCircle, IconSearch } from '@tabler/icons-react';

import useTemplatesModal from '../hooks/useTemplatesModal';
import TemplateCard from './components/TemplateCard';
import templates from '../templates';

const ALL_FILTER = 'all';

const filterOptions = [
    { label: 'Todos', value: ALL_FILTER },
    ...templates
        .map((t) => ({ label: t.indicatedFor, value: t.indicatedFor }))
        .filter((o, i, arr) => arr.findIndex((x) => x.value === o.value) === i),
];

export default function TemplatesModalView({ open, onClose, onSelectTemplate }) {
    const fileInputRef = useRef(null);
    const [search, setSearch] = useState('');
    const [activeFilter, setActiveFilter] = useState(ALL_FILTER);

    const {
        dragOver,
        error,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        handleFileInput,
        clearError,
    } = useTemplatesModal({ onSelectTemplate });

    const filteredTemplates = useMemo(() => templates.filter((t) => {
        const q = search.trim().toLowerCase();
        const matchesSearch = !q
            || t.title.toLowerCase().includes(q)
            || t.description.toLowerCase().includes(q);
        const matchesFilter = activeFilter === ALL_FILTER || t.indicatedFor === activeFilter;
        return matchesSearch && matchesFilter;
    }), [search, activeFilter]);

    return (
        <CustomModal
            open={open}
            handleClose={onClose}
            maxWidth={1160}
            hideSecondaryButton
            title="Criar formulário com um template"
            bodyComponent={
                <div className="templates-modal-body">
                    {/* ── File import ────────────────────────────────── */}
                    <div className="tm-section">
                        <Text.h5 weight={400} contrast={7}>Importar arquivo JSON</Text.h5>

                        <div
                            className={[
                                'tm-dropzone',
                                dragOver && 'tm-dropzone--active',
                                error && 'tm-dropzone--error',
                            ].filter(Boolean).join(' ')}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => { clearError(); fileInputRef.current?.click(); }}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".json,application/json"
                                style={{ display: 'none' }}
                                onChange={handleFileInput}
                            />

                            <div className="tm-dz-icon">
                                {error ? <IconAlertCircle size={24} /> : <IconFileUpload size={24} />}
                            </div>

                            <div className="tm-dz-text">
                                {error ? (
                                    <>
                                        <Text.span contrast={8} weight={500} className="tm-dz-error-title">
                                            Arquivo inválido
                                        </Text.span>
                                        <Text.span contrast={7} className="tm-dz-hint">{error}</Text.span>
                                        <Text.span contrast={4} className="tm-dz-hint">
                                            Clique para tentar outro arquivo
                                        </Text.span>
                                    </>
                                ) : (
                                    <>
                                        <Text.span contrast={7}>
                                            {dragOver
                                                ? 'Solte o arquivo aqui'
                                                : 'Arraste um arquivo .json ou clique para selecionar'}
                                        </Text.span>
                                        <Text.span contrast={4} className="tm-dz-hint">
                                            Use arquivos exportados pelo formulário ou no formato de template
                                        </Text.span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── Gallery ────────────────────────────────────── */}
                    <div className="tm-section">
                        <Text.h5 weight={400} contrast={7}>Modelos prontos</Text.h5>

                        <div className="tm-sticky-header">
                            <Input.Text
                                value={search}
                                onInput={setSearch}
                                placeholder="Buscar um modelo"
                                start={<IconSearch />}
                                fullWidth
                                autoFocus
                            />

                            <div className="tm-filters">
                                <ButtonGroup
                                    options={filterOptions}
                                    value={activeFilter}
                                    onChange={setActiveFilter}
                                    size="small"
                                />
                            </div>
                        </div>

                        {filteredTemplates.length > 0 ? (
                            <div className="tm-gallery">
                                {filteredTemplates.map((template) => (
                                    <TemplateCard
                                        key={template.id}
                                        template={template}
                                        onClick={() => onSelectTemplate?.(template.formData)}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="tm-empty">
                                <Text.span contrast={4}>Nenhum modelo encontrado para esta busca.</Text.span>
                            </div>
                        )}
                    </div>
                </div>
            }
        />
    );
}
